/**
 * NotificationsBell — the needs-attention model, surfaced in the top bar.
 *
 * The notifications derivation (`deriveNotifications` / `notificationCount`)
 * already existed but was invisible; this makes it FELT: a bell with a live
 * count badge and a glass dropdown listing everything that needs a human,
 * most-urgent-first. Each row deep-links to the surface that owns it (see
 * `notificationRoute`), so the bell is a router, not another inbox.
 *
 * Subscribes to the SAME live sources the Dashboard uses (accounts, tickets,
 * tasks, people, documents, approvals) and reuses the SAME pure helpers
 * (rankCustomersByRisk / getFollowUpReminders / scanSystem) — no duplicated
 * scoring, no new backend, and the count can never drift from the Dashboard.
 * Bootstraps are deliberately NOT called here; the Dashboard owns seeding.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  deriveNotifications,
  notificationCount,
  type MentionNotice,
  type NotificationSeverity,
  type NotificationsInput,
} from '../lib/crm/notifications';
import { notificationRoute } from '../lib/crm/notificationRoutes';
import { rankCustomersByRisk } from '../lib/crm/health';
import { getFollowUpReminders } from '../lib/crm/reminders';
import { scanSystem } from '../lib/agent/systemScanner';
import { subscribeApprovals } from '../lib/agent/approvals';
import { subscribeAccounts } from '../lib/db/accounts';
import { subscribeTickets } from '../lib/db/tickets';
import { subscribeTasks } from '../lib/db/tasks';
import { subscribeProjects } from '../lib/db/projects';
import { subscribePeople } from '../lib/db/people';
import { subscribeDocuments } from '../lib/db/documents';
import { useAuth } from '../lib/auth/AuthProvider';
import type {
  Account,
  ApprovalItem,
  BusinessDoc,
  Person,
  Project,
  ProjectTask,
  Ticket,
} from '../lib/db/types';
import type { ViewKey } from './TopMenu';
import type { ContactsTab } from '../views/PeopleView';

interface NotificationsBellProps {
  /** Navigate to a top-level view (App's setView, threaded via TopMenu). */
  onNavigate: (key: ViewKey) => void;
  /** Pre-select a Contacts sub-tab when a row lands on the CRM. */
  onSelectContactsTab?: (tab: ContactsTab) => void;
}

/** Severity → dot color, mirroring the Dashboard's finding palette. */
const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  high: C.magenta,
  medium: C.orangeWarm,
  low: C.blueElectric,
};

/** Dropdown width (px) — a touch wider than the nav flyouts for detail lines. */
const PANEL_W = 340;

/** Cap the visible rows; the badge still tells the full truth. */
const MAX_ROWS = 8;

export function NotificationsBell({
  onNavigate,
  onSelectContactsTab,
}: NotificationsBellProps) {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [documents, setDocuments] = useState<BusinessDoc[]>([]);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let active = true;
    const unsubs = [
      subscribeAccounts((r) => active && setAccounts(r)),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeTasks((r) => active && setTasks(r)),
      subscribeProjects((r) => active && setProjects(r)),
      subscribePeople((r) => active && setPeople(r)),
      subscribeDocuments((r) => active && setDocuments(r)),
      subscribeApprovals((r) => active && setApprovals(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  // One memo builds the input AND both derivations, so the badge and the
  // panel can never disagree. All helpers are pure; deriveNotifications
  // filters to pending approvals / at-risk band / overdue / high findings.
  const { notifications, total } = useMemo(() => {
    // @mentions of the viewer, resolved from real project + task comments.
    // A comment surfaces only when the viewer's name genuinely appears in
    // its parsed `mentions[]` — nothing inferred.
    const dn = (user?.displayName ?? '').toLowerCase();
    const fn = dn.split(' ')[0];
    const isMe = (m: string): boolean => {
      if (!fn) return false;
      const ml = m.toLowerCase();
      return ml === dn || ml === fn || ml.split(' ')[0] === fn;
    };
    const mentions: MentionNotice[] = [];
    for (const p of projects) {
      for (const c of p.comments ?? []) {
        if (c.mentions.some(isMe)) {
          mentions.push({
            id: `${p.id}:${c.id}`,
            authorName: c.authorName,
            context: `Project · ${p.title}`,
            excerpt: c.body.slice(0, 60),
            target: p.id,
            createdAt: c.createdAt,
          });
        }
      }
    }
    for (const t of tasks) {
      for (const c of t.comments ?? []) {
        if (c.mentions.some(isMe)) {
          mentions.push({
            id: `${t.id}:${c.id}`,
            authorName: c.authorName,
            context: `Task · ${t.title}`,
            excerpt: c.body.slice(0, 60),
            target: t.id,
            createdAt: c.createdAt,
          });
        }
      }
    }

    const input: NotificationsInput = {
      approvals,
      health: rankCustomersByRisk(people, { tasks }),
      reminders: getFollowUpReminders(people),
      findings: scanSystem({ tickets, tasks, people, accounts, documents }).findings,
      mentions,
    };
    return {
      notifications: deriveNotifications(input),
      total: notificationCount(input).total,
    };
  }, [approvals, people, tasks, projects, tickets, accounts, documents, user?.displayName]);

  // Anchor tracking + dismissal (outside click / Escape), portal-aware like
  // MenuDropdown so clicks inside the glass panel don't self-dismiss.
  useEffect(() => {
    if (!open) return;
    const updatePosition = () =>
      setRect(btnRef.current?.getBoundingClientRect() ?? null);
    updatePosition();
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !btnRef.current?.contains(target) &&
        !target.closest('[data-bell-portal="true"]')
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = notifications.slice(0, MAX_ROWS);
  const overflow = total - visible.length;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="topmenu-btn"
        data-open={open}
        aria-haspopup="menu"
        aria-expanded={open ? true : undefined}
        aria-label={
          total > 0
            ? `Notifications — ${total} need${total === 1 ? 's' : ''} attention`
            : 'Notifications — all clear'
        }
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
        style={{ position: 'relative' }}
      >
        <Bell size={14} strokeWidth={2.25} />
        {total > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 1,
              right: 1,
              minWidth: 15,
              height: 15,
              padding: '0 4px',
              borderRadius: 999,
              background: C.magenta,
              color: '#fff',
              fontSize: 9.5,
              fontWeight: 700,
              lineHeight: '15px',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            className="menu-dropdown-wrap"
            data-bell-portal="true"
            data-menu-portal="true"
            style={{
              position: 'fixed',
              top: rect.bottom + 12,
              // Right-aligned to the bell (it sits near the viewport edge),
              // clamped so the panel never bleeds off-screen left.
              left: Math.max(8, rect.right - PANEL_W),
              width: PANEL_W,
              zIndex: 60,
            }}
          >
            <GlassPanel
              variant="menu"
              className="menu-dropdown"
              animate={false}
              role="menu"
              aria-label="Notifications"
            >
              {visible.length === 0 ? (
                <div className="t-meta" style={{ padding: '14px 12px' }}>
                  All clear — nothing needs attention
                </div>
              ) : (
                visible.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className="menu-item"
                    role="menuitem"
                    onClick={() => {
                      const route = notificationRoute(n.kind);
                      if (route.contactsTab) onSelectContactsTab?.(route.contactsTab);
                      onNavigate(route.view);
                      setOpen(false);
                    }}
                  >
                    <span
                      className="menu-icon"
                      aria-label={`${n.severity} severity`}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 999,
                          background: SEVERITY_DOT[n.severity],
                          boxShadow: `0 0 8px ${SEVERITY_DOT[n.severity]}`,
                          display: 'inline-block',
                        }}
                      />
                    </span>
                    <span>
                      <span className="menu-title">{n.title}</span>
                      {n.detail && <span className="menu-sub">{n.detail}</span>}
                    </span>
                  </button>
                ))
              )}
              {overflow > 0 && (
                <div className="t-meta" style={{ padding: '8px 12px' }}>
                  +{overflow} more on the Dashboard
                </div>
              )}
            </GlassPanel>
          </div>,
          document.body,
        )}
    </>
  );
}
